import { createSlice } from "@reduxjs/toolkit";
import { Actor } from "../model/actorModel";
import { ActorSearchResult } from "../model/searchModel";
import { clearActorMergeSearch, getActorMergeActorDetails, searchActorMergeActors, ActorMergeSearchKey } from "../actions/actorMergeActions";

type ActorMergeSearchState = {
    loading: boolean;
    error: string | null;
    results: ActorSearchResult[];
    lastSearchText: string;
    limit: number;
};

interface ActorMergeState {
    searchByKey: Record<ActorMergeSearchKey, ActorMergeSearchState>;
    detailsById: Record<number, Actor>;
    detailsLoadingById: Record<number, boolean>;
    detailsErrorById: Record<number, string | null>;
}

const createInitialSearchState = (): ActorMergeSearchState => ({
    loading: false,
    error: null,
    results: [],
    lastSearchText: "",
    limit: 5
});

const initialState: ActorMergeState = {
    searchByKey: {
        target: createInitialSearchState(),
        source: createInitialSearchState()
    },
    detailsById: {},
    detailsLoadingById: {},
    detailsErrorById: {}
};

const actorMergeSlice = createSlice({
    name: "actorMerge",
    initialState,
    reducers: {},
    extraReducers: ({ addCase }) => {
        addCase(clearActorMergeSearch, (state, action) => {
            state.searchByKey[action.payload.key] = createInitialSearchState();
        });

        addCase(searchActorMergeActors.pending, (state, action) => {
            const key = action.meta.arg.key;
            const limit = action.meta.arg.limit ?? 5;
            state.searchByKey[key].loading = true;
            state.searchByKey[key].error = null;
            state.searchByKey[key].results = [];
            state.searchByKey[key].lastSearchText = action.meta.arg.searchText;
            state.searchByKey[key].limit = limit;
        });

        addCase(searchActorMergeActors.rejected, (state, action) => {
            const key = action.meta.arg.key;
            state.searchByKey[key].loading = false;
            state.searchByKey[key].results = [];
            state.searchByKey[key].error = (action.payload as string) ?? action.error.message ?? "Actor search failed";
        });

        addCase(searchActorMergeActors.fulfilled, (state, action) => {
            const { key, results, limit, searchText } = action.payload;
            state.searchByKey[key].loading = false;
            state.searchByKey[key].error = null;
            state.searchByKey[key].limit = limit;
            state.searchByKey[key].lastSearchText = searchText;
            state.searchByKey[key].results = results.slice(0, limit);
        });

        addCase(getActorMergeActorDetails.pending, (state, action) => {
            const actorId = action.meta.arg.actorId;
            state.detailsLoadingById[actorId] = true;
            state.detailsErrorById[actorId] = null;
        });

        addCase(getActorMergeActorDetails.rejected, (state, action) => {
            const actorId = action.meta.arg.actorId;
            state.detailsLoadingById[actorId] = false;
            state.detailsErrorById[actorId] = (action.payload as string) ?? action.error.message ?? "Failed to load actor details";
        });

        addCase(getActorMergeActorDetails.fulfilled, (state, action) => {
            const actor = action.payload;
            state.detailsById[actor.id] = actor;
            state.detailsLoadingById[actor.id] = false;
            state.detailsErrorById[actor.id] = null;
        });
    }
});

export default actorMergeSlice.reducer;
